import { COMMA, ENTER, TAB } from '@angular/cdk/keycodes';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { DepartmentOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { LcsSingleSelectSelectedEvent } from '../../single-select/single-select-base';
import { DepartmentSelectComponent } from '../component/department-select.component';
import { DepartmentChipInputOption } from './department.model';

@Component({
   providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: DepartmentChipInputComponent }],
   selector: 'ndt-department-chip-input',
   styleUrls: ['./department-chip-input.component.scss'],
   templateUrl: './department-chip-input.component.html',
   standalone: false,
})
export class DepartmentChipInputComponent implements ControlValueAccessor, AfterViewInit {
   @Input() formDisabled: boolean = false;

   @Input() required: boolean = false;

   @Input() accountId: number;

   @Input() extensionId: number;

   @Input() canCreateDepartments: boolean = false;

   @Input() maxChipCount: number | null = 1;

   @Input() set touched(touched: boolean) {
      if (touched === true && !this.departmentChipsControl.touched) {
         this.departmentChipsControl.markAsTouched();
      }
   }

   @ViewChild('departmentInput') departmentInput: ElementRef<HTMLInputElement>;

   @ViewChild('departmentSelect') autoComplete: DepartmentSelectComponent;

   departmentChipsControl = new UntypedFormControl();

   departmentInputControl = new UntypedFormControl();

   values: DepartmentChipInputOption[] = [];

   displayDepartments: DepartmentOption[] = [];

   get displayValues(): DepartmentChipInputOption[] {
      return this.values.filter((value) => !value.isDeleted);
   }

   separatorKeysCodes: number[] = [ENTER, COMMA, TAB];

   onChange: (values: DepartmentChipInputOption[]) => void;

   onTouched: () => void;

   constructor(private cdr: ChangeDetectorRef) {}

   ngAfterViewInit(): void {
      this.updateDepartmentInputControlDisabledState();
   }

   writeValue(values: DepartmentChipInputOption[]): void {
      if (values) {
         this.values = values;
      } else {
         this.values = [];
      }
      this.departmentChipsControl.setValue(values);
      this.removeErrors();
      this.updateDisplayDepartments();
      this.updateDepartmentInputControlDisabledState();
   }

   registerOnChange(onChange: (values: DepartmentChipInputOption[]) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   removeValue(value: DepartmentChipInputOption): void {
      const values: DepartmentChipInputOption[] = [...this.values];
      const indexToRemove = values.findIndex((v) => {
         return v.departmentExtensionId === value.departmentExtensionId && !v.isDeleted;
      });
      if (indexToRemove > -1) {
         values[indexToRemove].isDeleted = true;
         this.values = values;
         this.updateValueAndControls(this.values);
      }
   }

   add(event: MatChipInputEvent): void {
      //Don't add if there are more active chips than allowed
      if (this.maxChipCount && this.displayValues.length >= this.maxChipCount) {
         this.showTooManyDepartmentsError();
         return;
      }
      if (this.autoComplete.isOpen) {
         const value = event.value;
         const spacetrimmedValue = value.trim();
         const trimmedValue = spacetrimmedValue.substring(0, 50);
         if (trimmedValue) {
            const departmentOptionComponent = this.autoComplete.options.find((o) => {
               return (o.value && o.value.name && o.value.name.toLowerCase() === trimmedValue.toLowerCase()) || false;
            });
            if (departmentOptionComponent && departmentOptionComponent.value) {
               departmentOptionComponent.selectViaInteraction();
            } else {
               const existingDepartments = this.values
                  .filter((v) => !v.isDeleted)
                  .map((v) => v.department.name?.toLowerCase());
               if (existingDepartments.includes(trimmedValue.toLowerCase())) {
                  this.showDuplicateDepartmentError();
               } else {
                  if (!this.canCreateDepartments) {
                     this.showPrivilegeError();
                     return;
                  }
                  const matchingInactiveDepartment = this.autoComplete.inactiveDepartmentsList.find(
                     (d) => d.name?.toLowerCase() === trimmedValue.toLowerCase()
                  );
                  if (matchingInactiveDepartment) {
                     this.showInactiveDepartmentError();
                     return;
                  }
                  const newValue: DepartmentChipInputOption = {
                     departmentExtensionId: 0,
                     extensionId: this.extensionId,
                     departmentId: 0,
                     department: {
                        departmentId: 0,
                        name: trimmedValue,
                        isActive: true,
                        accountId: this.accountId,
                     },
                     isDeleted: false,
                  };
                  this.values = [...this.values, newValue];
                  this.updateValueAndControls(this.values);
               }
            }
         }
      }
   }

   selected(event: LcsSingleSelectSelectedEvent<DepartmentOption>): void {
      //Don't add if there are more active chips than allowed
      if (this.maxChipCount && this.displayValues.length >= this.maxChipCount) {
         return;
      }
      if (event.option.value) {
         const newValue: DepartmentChipInputOption = {
            departmentExtensionId: 0,
            extensionId: this.extensionId,
            departmentId: event.option.value.departmentId,
            department: {
               departmentId: event.option.value.departmentId,
               name: event.option.value.name as string,
               isActive: true,
               accountId: this.accountId,
            },
         };
         this.values = [...this.values, newValue];
         this.updateValueAndControls(this.values);
      }
   }

   removeErrors(): void {
      if (!this.departmentChipsControl.errors?.required) {
         this.departmentChipsControl.setErrors(null);
      }
   }

   showDuplicateDepartmentError(): void {
      this.departmentChipsControl.setErrors({ duplicateDepartment: true });
      this.departmentChipsControl.markAsTouched();
   }

   showInactiveDepartmentError(): void {
      this.departmentChipsControl.setErrors({ inactiveDepartment: true });
      this.departmentChipsControl.markAsTouched();
   }

   showPrivilegeError(): void {
      this.departmentChipsControl.setErrors({ privilegeError: true });
      this.departmentChipsControl.markAsTouched();
   }

   showTooManyDepartmentsError(): void {
      this.departmentChipsControl.setErrors({ tooManyDepartmentsError: true });
      this.departmentChipsControl.markAsTouched();
   }

   updateDisplayDepartments(): void {
      this.displayDepartments = this.displayValues.map((ext) => {
         return ext.department as DepartmentOption;
      });
   }

   updateValueAndControls(values: DepartmentChipInputOption[]): void {
      if (this.autoComplete.panel) {
         this.autoComplete.panel.visible = false;
      }
      this.departmentInput.nativeElement.value = '';
      this.departmentInputControl.setValue(null);
      this.departmentChipsControl.setValue(this.displayValues);
      this.onChange(this.values);
      this.removeErrors();
      this.updateDisplayDepartments();
      this.updateDepartmentInputControlDisabledState();

      // Manually trigger change detection. This synchronizes the view with the component's state
      // after synchronous changes (like disabling an input), preventing the NG0100 error.
      this.cdr.detectChanges();
   }

   private updateDepartmentInputControlDisabledState() {
      if (this.maxChipCount !== null && this.displayValues.length >= this.maxChipCount) {
         this.departmentInputControl.disable(); //this line is the problem
      } else {
         this.departmentInputControl.enable();
      }
   }
}
